/**
 * Real pin adapter for SceneTreeDropArea.
 *
 * Replaces `defaultMockPinAdapter` with a live call to
 * `POST /api/v1/gtwins/{gtwinId}/scene/items` via the shared
 * `gtwinClient` singleton. Payloads use the canonical contract
 * (`ARTIFACT_SCENE_CONTRACT` endpoint keys + G23 `crs`/`bbox`).
 */

import { gtwinClient } from '@/app/gtwinClientSingleton';
import {
  ARTIFACT_SCENE_CONTRACT,
  type ArtifactKind,
  type SceneItem,
  type SceneItemInput,
} from '@/sdk-stub/gtwin-sdk';
import { buildSceneItemInput } from '@/components/gtwin/buildSceneItemInput';
import type { PreflightInspection } from './preflightTypes';
import type { ReconciliationDecision } from '../crs/wizardTypes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Synthetic ULID-compatible ID generator (same approach as LayerDropZone). */
function generateSyntheticId(): string {
  const ts = Date.now().toString(36).toUpperCase().padStart(10, '0');
  const rand = Math.random().toString(36).slice(2, 18).toUpperCase().padStart(16, '0');
  return (ts + rand).slice(0, 26);
}

function pinArtifactKind(detectedKind: PreflightInspection['detectedKind']): ArtifactKind {
  if (detectedKind in ARTIFACT_SCENE_CONTRACT) {
    return detectedKind as ArtifactKind;
  }
  return 'pmtiles';
}

function buildPinPayload(
  inspection: PreflightInspection,
  decision: ReconciliationDecision | null,
): SceneItemInput {
  const artifactKind = pinArtifactKind(inspection.detectedKind);
  const url = inspection.url?.trim();
  if (!url) {
    throw new Error('Cannot pin: artifact URL is missing (convert first or drop a URL)');
  }

  const bbox =
    inspection.bbox && inspection.bbox.length === 4
      ? ([inspection.bbox[0], inspection.bbox[1], inspection.bbox[2], inspection.bbox[3]] as [
          number,
          number,
          number,
          number,
        ])
      : null;

  const built = buildSceneItemInput(
    {
      artifactKind,
      url,
      title: inspection.fileName ?? inspection.url,
      sourceCrs: inspection.crs?.code,
      bbox,
      id: inspection.id || generateSyntheticId(),
      extensions: decision
        ? {
            alignmentOptionKey: decision.optionKey,
            viewerMatrix: decision.viewerMatrix ?? undefined,
            scaleMode: decision.scaleMode,
          }
        : undefined,
    },
    { actor: 'drag-and-drop' },
  );

  if (built.errors.length > 0) {
    const summary = built.errors.map((e) => `${e.field}: ${e.message}`).join('; ');
    throw new Error(`Pin payload rejected by contract: ${summary}`);
  }

  const input = built.input;
  // G23a validates `crs` on the wire — mirror sourceCrs for server semantics.
  if (input.sourceCrs) {
    return { ...input, crs: input.sourceCrs } as SceneItemInput & { crs: string };
  }
  return input;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a real pin adapter bound to a specific gTwin ID.
 *
 * Returns a function matching the `SceneTreeDropAreaProps.pinAdapter`
 * signature so it can be passed directly as a prop.
 */
export function createRealPinAdapter(gtwinId: string) {
  return async function realPinAdapter(
    inspection: PreflightInspection,
    decision: ReconciliationDecision | null,
  ): Promise<SceneItem> {
    const item = buildPinPayload(inspection, decision);
    return gtwinClient.scene.items.add(gtwinId, item);
  };
}
